import React from 'react';
import styled from 'styled-components';
import {
  HeightProps,
  JustifyContentProps,
  MarginRightProps,
  WidthProps,
} from 'styled-system';

import Box from 'components/Box';
import { getImageSrc } from 'utils/getImageSrc';

import { Image } from './Image';
import { Placeholder } from './Placeholder';

const LogoBox = styled(Box)`
  img {
    max-width: 100%;
    max-height: 100%;
    font-size: 12px;
  }
`;

type LogoImageContentProps = WidthProps &
  HeightProps & {
    handle?: string;
    qaId?: string;
    fsWidth?: number;
    fsHeight?: number;
    mimetype?: string;
    src?: string;
    initial?: string;
    alt?: string;
    round?: boolean;
    fit?: string;
    noPlaceholder?: boolean;
  };

function LogoImageContent({
  handle,
  qaId,
  mimetype,
  src,
  initial,
  alt,
  round,
  fit,
  noPlaceholder,
  width,
  height,
  fsWidth,
  fsHeight,
}: LogoImageContentProps) {
  const imageWidth = fsWidth || (width as number);
  const imageHeight = fsHeight || (height as number);

  if (handle) {
    const src1x = getImageSrc({
      handle,
      mimetype: mimetype || '',
      width: imageWidth,
      height: imageHeight,
      round,
      fit,
      format: mimetype === 'image/tiff' ? 'jpg' : undefined,
    });

    const source2x = getImageSrc({
      handle,
      mimetype: mimetype || '',
      width: imageWidth ? imageWidth * 2 : undefined,
      height: imageHeight ? imageHeight * 2 : undefined,
      round,
      fit,
      format: mimetype === 'image/tiff' ? 'jpg' : undefined,
    });

    const source3x = getImageSrc({
      handle,
      mimetype: mimetype || '',
      width: imageWidth ? imageWidth * 3 : undefined,
      height: imageHeight ? imageHeight * 3 : undefined,
      round,
      fit,
      format: mimetype === 'image/tiff' ? 'jpg' : undefined,
    });

    return (
      <Image
        data-qa-id={qaId}
        layout="responsive"
        src={getImageSrc({
          handle,
          mimetype: mimetype || '',
          width: imageWidth,
          height: imageHeight,
          round,
          fit,
          format: mimetype === 'image/tiff' ? 'jpg' : undefined,
        })}
        srcSet={`${src1x},
                ${source2x} 2x,
                ${source3x} 3x`}
        alt={alt}
      />
    );
  }

  if (src) {
    return <Image data-qa-id={qaId} src={src} alt={alt} />;
  }

  if (noPlaceholder) return null;

  const getWidth = () => {
    if (typeof width === 'number' && typeof height === 'number')
      return Math.min(width, height);
    if (typeof fsWidth === 'number' && typeof fsHeight === 'number')
      return Math.min(fsWidth, fsHeight);
    return width || height;
  };

  return <Placeholder initial={initial || alt || ''} width={getWidth()} />;
}

export function LogoImage({
  width,
  height,
  fsWidth,
  fsHeight,
  handle,
  mimetype,
  src,
  initial,
  alt,
  round,
  fit,
  'data-qa-id': qaId,
  noPlaceholder,
  mr,
  justifyContent,
}: MarginRightProps &
  WidthProps &
  HeightProps &
  JustifyContentProps & {
    fsWidth?: number;
    fsHeight?: number;
    handle?: string;
    mimetype?: string;
    src?: string;
    initial?: string;
    alt?: string;
    round?: boolean;
    'data-qa-id'?: string;
    fit?: string;
    noPlaceholder?: boolean;
  }) {
  return (
    <LogoBox
      height={height}
      width={width}
      display="flex"
      alignItems="center"
      mr={mr}
      justifyContent={justifyContent || 'center'}
    >
      <LogoImageContent
        handle={handle}
        qaId={qaId}
        mimetype={mimetype}
        src={src}
        initial={initial}
        alt={alt}
        round={round}
        fit={fit}
        noPlaceholder={noPlaceholder}
        width={width}
        height={height}
        fsWidth={fsWidth}
        fsHeight={fsHeight}
      />
    </LogoBox>
  );
}
